import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";

import type { PageNode } from "@tento-ui/brand/page-model";
import { arrayProp, objectEntry, setTextOrHide, stringField, stringProp } from "./node-props";
import { tshVars } from "./tokens";

// The feature LIST — not a grid of cards.
//
// The current page puts these in three-up and two-up rounded white cards on a
// tinted band, which spends a lot of ink saying "these are separate" about
// things a reader wants to read straight down. A name at weight 700 and a body
// at 400, forty pixels apart, says the same thing with a line break.
//
// The names are `h2`, not `h3`: they sit under the page's `h1` with nothing
// between, and the most repeated element on a page should not create a heading
// level jump for every crawler that reads it.
@component({ name: "tsh-features" })
export class TshFeatures extends LonaWebComponent {
  private $heading: HTMLElement = this.$("heading");
  private $list: HTMLElement = this.$("list");

  bind(node: PageNode): void {
    setTextOrHide(this.$heading, stringProp(node, "heading"));
    DomUtils.clearChildren(this.$list);
    for (const raw of arrayProp(node, "features")) {
      const entry = objectEntry(raw);
      // `Option<T>` is `T | null | undefined` here, so `=== null` does not narrow
      // it — a truthiness guard is the one that excludes both.
      if (!entry) continue;
      const name = stringField(entry, "name");
      const body = stringField(entry, "description");
      if (!name) continue;

      // `.feature`, never `.row`: `Flexbox.style` is spliced into every shadow
      // root in this codebase and defines `.row { display: flex }`, so a class
      // named `row` here lays the list out horizontally and overlaps it. That
      // is not hypothetical — it happened once already, in the fx port.
      const $item = document.createElement("div");
      $item.className = "feature";

      const $name = document.createElement("h2");
      $name.textContent = name;
      $item.appendChild($name);

      if (body) {
        const $body = document.createElement("p");
        $body.textContent = body;
        $item.appendChild($body);
      }
      this.$list.appendChild($item);
    }
  }

  static $styles = [
    tshVars,
    css`
      :host {
        display: block;
        padding: 48px var(--tsh-gutter, 32px) 0;
        font-family: var(--tsh-mono);
        color: var(--tsh-muted, #525252);
      }

      #frame {
        max-width: var(--tsh-measure, 560px);
      }

      #heading {
        --font-size: var(--small-size);
        --line-height-ratio: 1.429;
        --margin-top: 0px;
        --margin-bottom: 29.2px; /* 24 box + 5.21 trim */
        color: var(--tsh-faint, #9e9e9e);
      }

      /* 40px of box between features. Authored 50.4 because both sides are
         trimmed: 40 + 5.21 (the h2 above) + 5.21 is 50.42, and at a bare 40 it
         renders 29 and the list closes up. */
      .feature + .feature {
        margin-top: 50.4px;
      }

      .feature h2 {
        --font-size: var(--h2-size);
        --line-height-ratio: 1.429;
        --margin-top: 0px;
        --margin-bottom: 16.7px; /* 4 box + 5.21 + 7.50 trims */
        font-weight: 700;
        color: var(--tsh-fg, #171717);
      }

      .feature p {
        --font-size: var(--p-size);
        --line-height-ratio: 1.75;
        --margin-top: 0px;
        --margin-bottom: 0px;
      }

      [hidden] {
        display: none !important;
      }
    `,
  ];

  static $html = html`
    <div id="frame">
      <p id="heading"></p>
      <div id="list"></div>
    </div>
  `;
}
